import styled, { keyframes } from "styled-components";

// Paleta alinhada com o tema da aplicação (verde Noordhen + cinzas).
// Mantida local para o modal continuar autocontido.
const COLORS = {
    border: "#dde3df",
    borderActive: "#2f8848",
    surface: "#f7faf8",
    surfaceActive: "#eaf6ee",
    text: "#333333",
    textMuted: "#6b756f",
    accent: "#28733d",
    accentSoft: "rgba(40, 115, 61, 0.12)",
    blue: "#2f80ed",
};

/* --------------------------------------------------------------------------
   Stepper — indicador visual de progresso entre os 4 passos.
   -------------------------------------------------------------------------- */

export const Stepper = styled.ol`
    display: flex;
    align-items: center;
    list-style: none;
    padding: 0 !important;
    margin: 0 !important;
    width: 100%;
`;

export const StepItem = styled.li`
    display: flex;
    align-items: center;
    flex: 1;
    padding: 0 !important;
    margin: 0 !important;

    &:last-child {
        flex: 0 0 auto;
    }

    .step-circle {
        width: 2.6rem;
        height: 2.6rem;
        border-radius: 50%;
        background: ${COLORS.border};
        color: ${COLORS.textMuted};
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 1.25rem;
        font-weight: 700;
        flex-shrink: 0;
        transition: background 220ms ease, color 220ms ease,
                    box-shadow 220ms ease, transform 220ms ease;
    }

    .step-label {
        margin-left: 0.7rem !important;
        font-size: 1.25rem;
        color: ${COLORS.textMuted};
        font-weight: 500;
        white-space: nowrap;
        transition: color 220ms ease, font-weight 220ms ease;
    }

    .step-connector {
        flex: 1;
        height: 2px;
        background: ${COLORS.border};
        margin: 0 1rem !important;
        border-radius: 2px;
        transition: background 220ms ease;
    }

    &.is-done .step-circle {
        background: ${COLORS.accent};
        color: #ffffff;
    }

    &.is-done .step-connector {
        background: ${COLORS.accent};
    }

    &.is-active .step-circle {
        background: ${COLORS.accent};
        color: #ffffff;
        box-shadow: 0 0 0 5px ${COLORS.accentSoft};
        transform: scale(1.06);
    }

    &.is-active .step-label {
        color: ${COLORS.accent};
        font-weight: 700;
    }
`;

/* --------------------------------------------------------------------------
   Dropzone — área de arrastar/soltar arquivo.
   -------------------------------------------------------------------------- */

const pulse = keyframes`
    0%   { transform: translateY(0); }
    50%  { transform: translateY(-3px); }
    100% { transform: translateY(0); }
`;

export const Dropzone = styled.div`
    position: relative;
    border: 2px dashed ${COLORS.border};
    border-radius: 12px;
    padding: 3.4rem 2rem !important;
    text-align: center;
    cursor: pointer;
    background-color: ${COLORS.surface};
    color: ${COLORS.textMuted};
    font-size: 1.5rem;
    line-height: 1.55;
    transition: background-color 200ms ease, border-color 200ms ease,
                color 200ms ease, transform 200ms ease,
                box-shadow 200ms ease;

    &:hover {
        border-color: ${COLORS.borderActive};
        color: ${COLORS.text};
        background-color: ${COLORS.surfaceActive};
    }

    &.active {
        background-color: ${COLORS.surfaceActive};
        border-color: ${COLORS.borderActive};
        color: ${COLORS.text};
        box-shadow: 0 0 0 6px ${COLORS.accentSoft};

        .dropzone-icon {
            animation: ${pulse} 900ms ease-in-out infinite;
        }
    }

    .dropzone-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 4.6rem;
        height: 4.6rem;
        margin: 0 auto 1rem auto !important;
        border-radius: 50%;
        background: #ffffff;
        color: ${COLORS.accent};
        box-shadow: 0 4px 10px rgba(15, 35, 25, 0.06);
        font-size: 2.2rem;
    }

    .dropzone-filename {
        display: inline-flex;
        align-items: center;
        gap: 0.6rem;
        padding: 0.6rem 1.2rem !important;
        margin-top: 0.4rem !important;
        background: #ffffff;
        border-radius: 999px;
        border: 1px solid ${COLORS.borderActive};
        color: ${COLORS.accent};
        font-weight: 600;
    }

    .dropzone-hint {
        display: block;
        margin-top: 0.4rem !important;
        font-size: 1.3rem;
        color: ${COLORS.textMuted};
    }

    code {
        background: rgba(0, 0, 0, 0.05);
        padding: 0.15rem 0.5rem !important;
        border-radius: 4px;
        font-size: 1.3rem;
        color: ${COLORS.accent};
    }
`;

/* --------------------------------------------------------------------------
   Cartão para baixar o template (mais convidativo que um link).
   -------------------------------------------------------------------------- */

export const TemplateCard = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.2rem;
    padding: 1.2rem 1.4rem !important;
    margin: 0 0 1.6rem 0 !important;
    border: 1px solid ${COLORS.border};
    border-radius: 10px;
    background: #ffffff;

    .template-info {
        display: flex;
        align-items: center;
        gap: 1rem;
        min-width: 0;
    }

    .template-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 3.6rem;
        height: 3.6rem;
        flex-shrink: 0;
        background: ${COLORS.accentSoft};
        color: ${COLORS.accent};
        border-radius: 8px;
        font-size: 1.8rem;
    }

    .template-text {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
        min-width: 0;
    }

    .template-title {
        font-weight: 700;
        color: ${COLORS.text};
        font-size: 1.5rem;
    }

    .template-subtitle {
        font-size: 1.3rem;
        color: ${COLORS.textMuted};
    }
`;

/* --------------------------------------------------------------------------
   Summary — chips de contagem por categoria.
   -------------------------------------------------------------------------- */

export const Summary = styled.div`
    display: flex;
    gap: 0.8rem;
    flex-wrap: wrap;
    margin: 0 0 1.6rem 0 !important;

    span {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1.1rem !important;
        border-radius: 999px;
        background: ${COLORS.surface};
        border: 1px solid ${COLORS.border};
        font-weight: 600;
        font-size: 1.3rem;
        color: ${COLORS.text};
        line-height: 1;
    }
`;

/* --------------------------------------------------------------------------
   ProgressBar — barra usada no passo 4.
   -------------------------------------------------------------------------- */

export const ProgressBar = styled.div`
    width: 100%;
    height: 12px;
    background: ${COLORS.surface};
    border-radius: 999px;
    overflow: hidden;
    border: 1px solid ${COLORS.border};

    > div {
        height: 100%;
        background: linear-gradient(90deg, #2f8848 0%, #28733d 100%);
        transition: width 220ms ease-out;
    }
`;

/* --------------------------------------------------------------------------
   FilterBar — controles de filtro no passo 2.
   -------------------------------------------------------------------------- */

export const FilterBar = styled.div`
    display: flex;
    align-items: center;
    gap: 1.4rem;
    margin: 0 0 1rem 0 !important;
    flex-wrap: wrap;

    select.form-control {
        height: auto;
        padding: 0.6rem 1rem !important;
        border-radius: 8px;
        border: 1px solid ${COLORS.border};
        font-size: 1.4rem;
        color: ${COLORS.text};
    }

    label {
        font-size: 1.4rem;
        color: ${COLORS.text};
        cursor: pointer;
    }
`;

/* --------------------------------------------------------------------------
   PreviewTable — tabela com as linhas do plano de importação.
   -------------------------------------------------------------------------- */

export const PreviewTable = styled.div`
    max-height: 36rem;
    overflow: auto;
    border: 1px solid ${COLORS.border};
    border-radius: 10px;
    background: #ffffff;

    table {
        width: 100%;
        border-collapse: collapse;
        font-size: 1.35rem;
    }

    thead th {
        position: sticky;
        top: 0;
        background: ${COLORS.surface};
        font-weight: 700;
        text-align: left;
        padding: 0.9rem 1.2rem !important;
        border-bottom: 1px solid ${COLORS.border};
        color: ${COLORS.text};
        font-size: 1.3rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    tbody td {
        padding: 0.85rem 1.2rem !important;
        border-bottom: 1px solid #f0f3f1;
        color: ${COLORS.text};
        vertical-align: middle;
    }

    tbody tr:last-child td {
        border-bottom: 0;
    }

    tbody tr:hover {
        background: ${COLORS.surface};
    }

    tbody tr.has-warning td {
        border-bottom: 0;
    }

    tbody tr.has-warning td:first-child {
        border-left: 3px solid #f2994a;
        padding-left: 0.9rem !important;
    }

    tbody tr.warning-details td {
        background: #fff8e1;
        border-left: 3px solid #f2994a;
        border-bottom: 1px solid #f0d99a;
        padding: 0.5rem 1.2rem 0.9rem 0.9rem !important;
    }

    tbody tr.warning-details ul {
        list-style: none;
        padding: 0 !important;
        margin: 0 !important;
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
    }

    tbody tr.warning-details li {
        display: flex;
        align-items: flex-start;
        gap: 0.55rem;
        font-size: 1.25rem;
        line-height: 1.45;
        color: #8a5a00;
        margin: 0 !important;
    }

    tbody tr.warning-details li svg {
        flex-shrink: 0;
        color: #f2994a;
        margin-top: 0.2rem !important;
        width: 1.4rem;
        height: 1.4rem;
    }
`;

/* --------------------------------------------------------------------------
   ActionBadge — etiqueta por ação (criar / atualizar / ignorar / erro).
   -------------------------------------------------------------------------- */

const TONES = {
    create: { fg: "#1e8e3e", bg: "#e6f4ea" },
    update: { fg: "#1967d2", bg: "#e8f0fe" },
    skip: { fg: "#b06000", bg: "#fff8e1" },
    error: { fg: "#d93025", bg: "#fce8e6" },
};

export const ActionBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0.9rem !important;
    border-radius: 999px;
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 1.7;
    color: ${(p) => TONES[p.$tone]?.fg || COLORS.text};
    background: ${(p) => TONES[p.$tone]?.bg || "#eee"};

    span {
        font-size: 1.3rem;
        line-height: 1;
    }
`;

/* --------------------------------------------------------------------------
   StepIntro — bloco com texto introdutório de cada passo.
   -------------------------------------------------------------------------- */

export const StepIntro = styled.div`
    margin: 0 0 1.6rem 0 !important;
    color: ${COLORS.text};

    p {
        margin: 0 !important;
        font-size: 1.5rem;
    }

    strong {
        color: ${COLORS.accent};
    }
`;

/* --------------------------------------------------------------------------
   WarningCallout — bloco de "atenção" usado no passo 3.
   -------------------------------------------------------------------------- */

export const WarningCallout = styled.div`
    margin: 0 0 0.5rem 0 !important;
    padding: 1.2rem 1.4rem !important;
    background: #fff8e1;
    border: 1px solid #f5d27a;
    border-left: 4px solid #f2994a;
    border-radius: 8px;
    color: ${COLORS.text};
    font-size: 1.4rem;

    .callout-title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-weight: 700;
        color: #8a5a00;
        margin-bottom: 0.6rem !important;
    }

    ul {
        margin: 0 !important;
        padding-left: 1.8rem !important;
    }

    li {
        margin: 0 0 0.3rem 0 !important;
    }

    li:last-child {
        margin-bottom: 0 !important;
    }

    code {
        background: rgba(0, 0, 0, 0.06);
        padding: 0.1rem 0.5rem !important;
        border-radius: 4px;
        font-size: 1.25rem;
    }
`;
